import clsx from 'clsx';
import React, { createContext, useContext } from 'react';
import type {
  Checklist,
  ChecklistItem,
} from '../../../features/onboarding/onboardingChecklist';

/**
 * Getting started is one page: every step opens in place under its row.
 * A step talks to the page through this context, to open another step
 * ("answer them first") or to put itself away ("Not yet").
 */
export interface StepFlow {
  open: (key: string) => void;
  close: () => void;
}

const noop = () => undefined;
const StepFlowContext = createContext<StepFlow>({ open: noop, close: noop });

export const StepFlowProvider = StepFlowContext.Provider;
export const useStepFlow = () => useContext(StepFlowContext);

/** What every step panel is given. */
export interface StepProps {
  item: ChecklistItem;
  checklist: Checklist;
}

/** A step's intro line: what it's for, in a sentence or two. */
export const StepIntro: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <p className="mb-5 max-w-[68ch] text-v2-13 leading-[20px] text-v2-fg-2">
    {children}
  </p>
);

/**
 * How every step ends: one row under a hairline holding the step's single
 * primary action, with the "Saved." note (or a Reason) inline beside it.
 * Steps share this so intro → work → footer reads the same all the way down.
 */
export const StepFooter: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <div
    className={clsx(
      'mt-6 flex flex-wrap items-center gap-3 border-t border-v2-line/80 pt-4',
      className,
    )}
  >
    {children}
  </div>
);

/**
 * One part of a longer step (Test, Go live): a heading and its content,
 * set off from the part before by a hairline. Parts replace the cards
 * these screens had on their own pages, so a step never nests cards.
 */
export const StepPart: React.FC<{
  title?: React.ReactNode;
  /** Right of the title: a count, a note. */
  aside?: React.ReactNode;
  /** A numbered sub-step: its number, beside the title. */
  n?: number;
  'aria-label'?: string;
  children: React.ReactNode;
}> = ({ title, aside, n, 'aria-label': ariaLabel, children }) => (
  <section
    aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
    className="flex gap-3.5 border-t border-v2-line/80 pt-5 first:border-t-0 first:pt-0 [&+&]:mt-5"
  >
    {n !== undefined && (
      <span
        aria-hidden
        className="v2-num mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-v2-accent/10 text-v2-12 font-semibold text-v2-accent"
      >
        {n}
      </span>
    )}
    <div className="min-w-0 flex-1">
      {title && (
        <div className="mb-2.5 flex items-baseline justify-between gap-3">
          <h4 className="text-v2-14 font-semibold text-v2-fg">{title}</h4>
          {aside && <span className="text-v2-12 text-v2-fg-3">{aside}</span>}
        </div>
      )}
      {children}
    </div>
  </section>
);
